import { action, computed, observable } from 'mobx';

import { events } from '@estee/elc-service-bus';
import { ICart } from '@estee/elc-service-bus/typings/src/service-bus-topics/events/CartInterfaces';

import UpdateOfferUseCase from '~use-cases/UpdateOfferUseCase';
import { OfferActionsEnum, OfferTypesEnum } from '~utils/OffersEnum';
import { diContainer } from '~setup/diContainer';
import { serviceNames } from '~setup/OffersService';

import { eventEmitter } from '~sharedComponents/EventAdapters';

import Offer from '~domain/entities/Offer/Offer';
import IOffer, { IOfferMessage } from '~domain/entities/Offer/IOffer';
import OffersRepository from '~repositories/OffersRepository/OffersRepository';
import { IOffersServiceConfigs } from '../../internal/domain/entities/Config/IConfig';
import FetchedOffersByViewUseCase from '../../internal/use-cases/FetchedOffersByViewUseCase';

export interface OfferCodeModuleControllerProps {
    offersRepository: OffersRepository;
}

export default class OfferCodeModuleViewController {
    @observable private _offerCode: string;
    @observable private _offersRepository: OffersRepository;
    @observable private _isLoading: boolean;
    @observable private _errorMessage: string;
    @observable private _appliedOfferCode: string;
    @observable private _messages: IOfferMessage[];

    constructor({ offersRepository }: OfferCodeModuleControllerProps) {
        this._offerCode = '';
        this._messages = [];
        this._errorMessage = '';
        this._isLoading = false;
        this._offersRepository = offersRepository;
        this._offersRepository.setEnabledViews([Offer.appliedOffers]);
    }

    @action
    public loaded = async (propsConfig: IOffersServiceConfigs) => {
        this.isLoading = true;
        diContainer.get(serviceNames.config).updateConfig({ appConfig: propsConfig });

        await new FetchedOffersByViewUseCase({ views: [Offer.appliedOffers] }).execute();
        this.isLoading = false;
    };

    @action applyOfferCode = async () => {
        this.clearFailMessages();
        this._isLoading = true;
        this._appliedOfferCode = this._offerCode?.trim()?.toLowerCase();

        const payload = {
            offerCode: this._appliedOfferCode,
            action: OfferActionsEnum.apply,
            views: [Offer.appliedOffers]
        };

        try {
            this._offersRepository.cartOffers.appliedOffers.hasError = false;

            const data = await new UpdateOfferUseCase(payload).execute();

            const appliedOffer =
                this._appliedOfferCode in data
                    ? data[this._appliedOfferCode]
                    : Object.values(data)?.[0];

            const { messages = [] } = appliedOffer || {};
            const { error_message = '' } = appliedOffer?.messaging || {};
            const inCart = appliedOffer?.flags?.inCart;
            const isOfferApplied = this.isOfferCodeApplied(this._appliedOfferCode);

            inCart || isOfferApplied
                ? this.offerApplySuccess()
                : this.offerApplyFail(error_message, messages);
        } catch {
            this._offersRepository.cartOffers.appliedOffers.hasError = true;
        }

        this._isLoading = false;
        eventEmitter(events.OFFER_CODE_SUCCEDED, {});
    };

    @action public clearErrorMessage = () => (this._errorMessage = '');
    @action public clearOfferCode = () => (this._offerCode = '');
    @action public clearMessages = () => (this._messages = []);

    @action public clearFailMessages = () => this.clearErrorMessage() || this.clearMessages();

    @action public setMessages = (messages: IOfferMessage[]) => (this._messages = messages);
    @action public setErrorMessage = (message: string) => (this._errorMessage = message);
    @action public setOfferCode = (value: React.ChangeEvent<HTMLInputElement>) =>
        (this._offerCode = value?.target?.value || '');

    @action public removeOffer = async ({ offerCode }: IOffer) => {
        await new UpdateOfferUseCase({ offerCode, action: OfferActionsEnum.remove }).execute();
        eventEmitter(events.CART_UPDATED, {} as ICart);
        window.dispatchEvent(new CustomEvent('cart.offerRemove', { detail: offerCode }));
    };

    @computed public get messages() {
        return this._messages;
    }

    @computed public get errorMessage() {
        return this._errorMessage;
    }

    @computed public get isLoading() {
        return this._isLoading;
    }

    public set isLoading(newState) {
        this._isLoading = newState;
    }

    @computed public get getOfferCode() {
        return this._offerCode;
    }

    @computed public get getAppliedOfferCode() {
        return this._appliedOfferCode;
    }

    @computed public get getAppliedOffers() {
        return (this._offersRepository?.cartOffers?.appliedOffers?.offers || []).filter(
            ({ offerType }) => offerType?.toLowerCase?.() === OfferTypesEnum.manual
        );
    }

    @computed public get showError() {
        return (
            this._offersRepository.cartOffers.appliedOffers.hasError &&
            this._appliedOfferCode &&
            !this._isLoading
        );
    }

    private isOfferCodeApplied = (offerCodeToCheck: string) =>
        this._offersRepository.cartOffers.appliedOffers?.offers?.find(
            ({ offerCode }) => offerCode === offerCodeToCheck
        );

    private offerApplySuccess = () => {
        this.clearFailMessages();
        this.clearOfferCode();

        eventEmitter(events.CART_UPDATED, {} as ICart);
        window.dispatchEvent(
            new CustomEvent('cart.offerSuccess', { detail: this._appliedOfferCode })
        );
    };

    private offerApplyFail = (errorMessage: string, messages?: IOfferMessage[]) => {
        if (messages?.length) {
            this.setMessages(messages);
        }

        this.setErrorMessage(errorMessage);

        this._offersRepository.cartOffers.appliedOffers.hasError = true;
        window.dispatchEvent(new CustomEvent('cart.offerFail', { detail: this._appliedOfferCode }));
    };
}
